import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import { toast } from "react-toastify";
import { 
  PersonAdd, 
  Close,
  Person
} from '@material-ui/icons';
import ClientService from '../services/ClientService';
import TypeClientService from '../services/TypeClientService';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#226D68',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  submitButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(34, 109, 104, 0.3)',
    textTransform: 'none',
    minWidth: 200,
    '&:hover': {
      background: 'linear-gradient(135deg, #1a5a55 0%, #226D68 100%)',
      boxShadow: '0 6px 20px rgba(34, 109, 104, 0.4)',
    },
  },
  addButton: {
    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #F57C00 0%, #EF6C00 100%)',
    },
  },
  beneficiaireCard: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const ClientUpdateForm = ({ client, onClose, onSuccess }) => {
  const classes = useStyles();
  const theme = useTheme();
  const auth = useSelector((state) => state.auth);
  const [typesClient, setTypesClient] = useState([]);
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [showBeneficiaireForm, setShowBeneficiaireForm] = useState(false);
  const [currentBeneficiaire, setCurrentBeneficiaire] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    lien_parente: '',
    telephone: ''
  });

  // Récupérer le client depuis le tableau
  const currentClient = client && client.length > 0 ? client[0] : null;

  const liensParente = [
    { value: 'EPOUX', label: 'Époux/Épouse' },
    { value: 'ENFANT', label: 'Enfant' },
    { value: 'PARENT', label: 'Parent' },
    { value: 'FRERE_SOEUR', label: 'Frère/Sœur' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await TypeClientService.getTypeClient();
        setTypesClient(response.data || []);
        
        console.log("Client reçu dans update form:", currentClient);
        console.log("Bénéficiaires du client:", currentClient?.beneficiaires);
        
        if (currentClient) {
          // Utiliser les bénéficiaires déjà chargés depuis l'API /all
          const beneficiairesAvecId = (currentClient?.beneficiaires || []).map(benef => ({
            ...benef,
            id: benef.idBeneficiaire || Date.now() + Math.random()
          }));
          
          setBeneficiaires(beneficiairesAvecId);
          
          // Calculer le pourcentage total initial
          setTotalPercentage(calculateTotalPercentage(currentClient.remise, currentClient.tauxIpm));
        }
      } catch (error) {
        console.error('Erreur chargement données:', error);
      }
    };
    fetchData();
  }, [currentClient]);

  // Vérifier si le client existe avant de définir les valeurs initiales
  const initialValues = currentClient ? {
    idType: currentClient.idType || '',
    reference: currentClient.reference || '',
    nom: currentClient.nom || '',
    adresse: currentClient.adresse || '',
    telephone: currentClient.telephone || '',
    quota: currentClient.quota || '',
    remise: currentClient.remise || 0,
    tauxIpm: currentClient.tauxIpm || 0,
    speudo: auth.nom || ''
  } : {
    idType: '',
    reference: '',
    nom: '',
    adresse: '',
    telephone: '',
    quota: '',
    remise: 0,
    tauxIpm: 0,
    speudo: auth.nom || ''
  };

  const validationSchema = Yup.object().shape({
    idType: Yup.number().required("Type de client requis"),
    reference: Yup.string(),
    nom: Yup.string().required("Nom requis").min(2, "Le nom doit contenir au moins 2 caractères"),
    adresse: Yup.string().required("Adresse requise").min(5, "L'adresse doit contenir au moins 5 caractères"),
    telephone: Yup.string()
      .matches(/^[0-9+]+$/, "Numéro de téléphone invalide")
      .min(8, "Le téléphone doit avoir au moins 8 chiffres")
      .required("Téléphone requis"),
    quota: Yup.number().min(0, "Le quota ne peut pas être négatif"),
    remise: Yup.number().min(0, "Ne peut pas être négatif").max(100, "Ne peut pas dépasser 100%"),
    tauxIpm: Yup.number().min(0, "Ne peut pas être négatif").max(100, "Ne peut pas dépasser 100%")
  });

  const calculateTotalPercentage = (remise, tauxIpm) => {
    return (parseFloat(remise) || 0) + (parseFloat(tauxIpm) || 0);
  };

  const getRemiseLabel = (idType) => {
    const labels = {
      1: 'Taux de remise (%)',
      2: 'Taux de participation client (%)'
    };
    return labels[idType] || 'Remise (%)';
  };

  const handleAddBeneficiaire = () => {
    if (!currentBeneficiaire.nom || !currentBeneficiaire.prenom || !currentBeneficiaire.lien_parente) {
      toast.error("Veuillez remplir les champs obligatoires du bénéficiaire");
      return;
    }

    const formattedDate = currentBeneficiaire.date_naissance 
      ? new Date(currentBeneficiaire.date_naissance).toISOString().split('T')[0]
      : null;

    const newBeneficiaire = {
      nom: currentBeneficiaire.nom,
      prenom: currentBeneficiaire.prenom,
      date_naissance: formattedDate,
      lien_parente: currentBeneficiaire.lien_parente,
      telephone: currentBeneficiaire.telephone
    };

    setBeneficiaires(prev => [...prev, newBeneficiaire]);
    setCurrentBeneficiaire({
      nom: '',
      prenom: '',
      date_naissance: '',
      lien_parente: '',
      telephone: ''
    });
    setShowBeneficiaireForm(false);
    toast.success("Bénéficiaire ajouté avec succès");
  };

  const handleRemoveBeneficiaire = (id) => {
    setBeneficiaires(prev => prev.filter(b => b.id !== id));
  };

  const getLienParenteLabel = (value) => {
    const lien = liensParente.find(l => l.value === value);
    return lien ? lien.label : value;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    try {

      const remiseValue = values.remise ? parseFloat(values.remise) : 0;
      const tauxIpmValue = values.tauxIpm ? parseFloat(values.tauxIpm) : 0;
      const totalPercent = calculateTotalPercentage(values.remise, values.tauxIpm);
      
      if (totalPercent > 100) {
        toast.error(`Le pourcentage total (${totalPercent}%) dépasse 100%`);
        setSubmitting(false);
        return;
      }

      // Préparer les données pour l'API
      const clientData = {
        ...values,
        remise: remiseValue, 
        tauxIpm: tauxIpmValue,
        speudo: auth.nom,
        beneficiaires: beneficiaires.map(benef => ({
            ...(benef.idBeneficiaire && { idBeneficiaire: benef.idBeneficiaire }),
            nom: benef.nom,
          prenom: benef.prenom,
          date_naissance: benef.date_naissance 
            ? new Date(benef.date_naissance).toISOString().split('T')[0] 
            : null,
          lien_parente: benef.lien_parente,
          telephone: benef.telephone
        }))
      };

      console.log("Données envoyées à l'API:", clientData);

      // Utiliser l'ID du client actuel
      await ClientService.updateClient(clientData, currentClient.idClient);
      toast.success("Client modifié avec succès");
      onSuccess();
      
    } catch (error) {
      console.error('Erreur modification client:', error);
      toast.error(error.response?.data || "Erreur lors de la modification");
    } finally {
      setSubmitting(false);
    }
  };

  // Afficher un message si aucun client n'est trouvé
  if (!currentClient) {
    return (
      <Box className={classes.formContainer}>
        <Typography variant="h6" color="error" align="center">
          Aucun client trouvé pour la modification
        </Typography>
        <Box display="flex" justifyContent="center" mt={2}>
          <Button onClick={onClose} variant="contained" color="primary">
            Fermer
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.formContainer}>
      <Formik 
        initialValues={initialValues} 
        validationSchema={validationSchema} 
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              
              {/* Section Informations de base */}
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Informations générales
                </Typography>
              </Grid>

              {/* Type de client */}
              <Grid item xs={12} md={6}>
                <Field name="idType">
                  {({ field, form }) => (
                    <TextField
                      {...field}
                      select
                      label="Type de client"
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={touched.idType && !!errors.idType}
                      helperText={touched.idType && errors.idType}
                      required
                    >
                      <MenuItem value="">
                        <em>Sélectionnez un type</em>
                      </MenuItem>
                      {typesClient.map((type) => (
                        <MenuItem key={type.idType} value={type.idType}>
                          {type.designation}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>
              </Grid>

              {/* Référence */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="reference"
                  label="Référence"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.reference && !!errors.reference}
                  helperText={touched.reference && errors.reference}
                />
              </Grid>

              {/* Nom */}
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="nom"
                  label="Nom complet"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.nom && !!errors.nom}
                  helperText={touched.nom && errors.nom}
                  required
                />
              </Grid>

              {/* Section Contact */}
              <Grid item xs={12}>
                <Typography className={classes.sectionTitle}>
                  Informations de contact
                </Typography>
              </Grid>

              {/* Adresse */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="adresse"
                  label="Adresse"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.adresse && !!errors.adresse}
                  helperText={touched.adresse && errors.adresse}
                  required
                />
              </Grid>

              {/* Téléphone */}
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  name="telephone"
                  label="Téléphone"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.telephone && !!errors.telephone}
                  helperText={touched.telephone && errors.telephone}
                  required
                />
              </Grid>

              {/* Quota */}
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  name="quota"
                  label="Quota"
                  type="number"
                  variant="outlined"
                  fullWidth
                  size="small"
                  error={touched.quota && !!errors.quota}
                  helperText={touched.quota && errors.quota}
                />
              </Grid>

              {/* Section Paramètres financiers */}
              {values.idType && (
                <Grid item xs={12}>
                  <Typography className={classes.sectionTitle}>
                    Paramètres financiers
                  </Typography>
                </Grid>
              )}

              {values.idType && (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={values.idType === '2' ? 6 : 12}>
                    <Field
                      as={TextField}
                      name="remise"
                      label={getRemiseLabel(values.idType)}
                      type="number"
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={touched.remise && !!errors.remise}
                      helperText={touched.remise && errors.remise}
                      onChange={(e) => {
                        setFieldValue("remise", e.target.value);
                        setTotalPercentage(calculateTotalPercentage(e.target.value, values.tauxIpm));
                      }}
                    />
                  </Grid>

                  {values.idType === '2' && (
                    <Grid item xs={12} md={6}>
                      <Field
                        as={TextField}
                        name="tauxIpm"
                        label="Taux de participation IPM (%)"
                        type="number"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={touched.tauxIpm && !!errors.tauxIpm}
                        helperText={touched.tauxIpm && errors.tauxIpm}
                        onChange={(e) => {
                          setFieldValue("tauxIpm", e.target.value);
                          setTotalPercentage(calculateTotalPercentage(values.remise, e.target.value));
                        }}
                      />
                    </Grid>
                  )}

                  {(values.remise || values.tauxIpm) && (
                    <Grid item xs={12}>
                      <Alert 
                        severity={totalPercentage > 100 ? "error" : totalPercentage === 100 ? "success" : "info"}
                        variant="outlined"
                      >
                        Pourcentage total: {totalPercentage}%
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* Section Bénéficiaires */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography className={classes.sectionTitle}>
                    <Person style={{ marginRight: theme.spacing(1) }} />
                    Bénéficiaires ({beneficiaires.length})
                  </Typography>
                  <Button
                    startIcon={<PersonAdd />}
                    variant="contained"
                    className={classes.addButton}
                    onClick={() => setShowBeneficiaireForm(true)}
                    size="small"
                  >
                    Ajouter un bénéficiaire
                  </Button>
                </Box>

                {/* Formulaire d'ajout de bénéficiaire */}
                {showBeneficiaireForm && (
                  <Card className={classes.beneficiaireCard}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Nouveau bénéficiaire</Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => setShowBeneficiaireForm(false)}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Nom"
                            value={currentBeneficiaire.nom}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              nom: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Prénom"
                            value={currentBeneficiaire.prenom}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              prenom: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            select
                            label="Lien de parenté"
                            value={currentBeneficiaire.lien_parente}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              lien_parente: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            required
                          >
                            <MenuItem value="">
                              <em>Sélectionnez un lien</em>
                            </MenuItem>
                            {liensParente.map((lien) => (
                              <MenuItem key={lien.value} value={lien.value}>
                                {lien.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Téléphone"
                            value={currentBeneficiaire.telephone}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              telephone: e.target.value
                            }))}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Date de naissance"
                            type="date"
                            value={currentBeneficiaire.date_naissance}
                            onChange={(e) => setCurrentBeneficiaire(prev => ({
                              ...prev,
                              date_naissance: e.target.value
                            }))}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddBeneficiaire}
                            fullWidth
                          >
                            Ajouter le bénéficiaire
                          </Button>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                {/* Liste des bénéficiaires */}
                {beneficiaires.length > 0 && (
                  <Box className={classes.chipContainer}>
                    {beneficiaires.map((beneficiaire) => (
                      <Chip
                        key={beneficiaire.idBeneficiaire}
                        label={`${beneficiaire.nom} ${beneficiaire.prenom} (${getLienParenteLabel(beneficiaire.lien_parente)})`}
                        onDelete={() => handleRemoveBeneficiaire(beneficiaire.idBeneficiaire)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

                {beneficiaires.length === 0 && (
                  <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center', padding: theme.spacing(2) }}>
                    Aucun bénéficiaire enregistré pour ce client
                  </Typography>
                )}
              </Grid>

              {/* Boutons d'action */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" mt={4}>
                  <Button 
                    onClick={onClose}
                    variant="outlined"
                    size="large"
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    className={classes.submitButton}
                    disabled={isSubmitting}
                    size="large"
                  >
                    {isSubmitting ? 'Modification en cours...' : 'Modifier le client'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ClientUpdateForm;